import styled from 'styled-components';

export const PRIMARY = '#1f2937';
export const ACCENT = '#E31B23';
export const ACCENT_SOFT = 'rgba(227, 27, 35, 0.08)';
export const BORDER = '#eef0f3';
export const BG = '#f6f8fb';
export const MUTED = '#6b7280';

export const BuilderShell = styled.div`
  height: calc(100vh - 90px);
  display: flex;
  flex-direction: column;
  background: ${BG};
  border-radius: 12px;
  border: 1px solid ${BORDER};
  overflow: hidden;
  min-height: 0;

  /* On mobile fill the parent ContentScrollArea exactly so only the
     inner Body scrolls — no double / page-level scroll. */
  @media (max-width: 900px) {
    height: 100%;
    max-height: 100%;
    border-radius: 8px;
  }
`;

export const TopBar = styled.div`
  background: #fff;
  padding: 14px 24px;
  border-bottom: 1px solid ${BORDER};
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;

  .left { display: flex; align-items: center; gap: 12px; min-width: 0; }
  .right { display: flex; align-items: center; gap: 10px; }
  h2 {
    margin: 0; font-size: 18px; color: ${PRIMARY}; font-weight: 600;
    white-space: nowrap; overflow: hidden; text-overflow: ellipsis;
  }
  .ant-btn { border-radius: 8px; height: 36px; }
  .step-pill {
    display: none;
    font-size: 12px; font-weight: 600; color: ${PRIMARY};
    background: #f1f5f9; padding: 4px 10px; border-radius: 999px;
    white-space: nowrap;
  }

  @media (max-width: 900px) {
    padding: 10px 14px;
    gap: 8px;
    .left { gap: 8px; flex: 1; min-width: 0; }
    h2 { font-size: 15px; }
    .ant-btn { height: 32px; }
    .step-pill { display: inline-flex; }
    .hide-mobile { display: none !important; }
  }
`;

export const StepDots = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  font-size: 13px;
  color: ${MUTED};

  .dot {
    width: 28px; height: 28px; border-radius: 50%;
    background: #f1f5f9; color: ${MUTED};
    display: inline-flex; align-items: center; justify-content: center;
    font-weight: 600; font-size: 12px;
    border: 1px solid ${BORDER};
  }
  .dot.active { background: ${PRIMARY}; color: #fff; border-color: ${PRIMARY}; }
  .dot.done { background: #10b981; color: #fff; border-color: #10b981; }
  .label { font-weight: 500; color: ${PRIMARY}; }
  .label.muted { color: ${MUTED}; font-weight: 400; }
  .sep { color: #cbd5e1; }
`;

export const Body = styled.div`
  flex: 1;
  overflow: auto;
  padding: 24px 32px;
  min-height: 0;

  &.no-padding { padding: 0; overflow: hidden; }

  @media (max-width: 900px) {
    padding: 16px;
  }
`;

export const FooterBar = styled.div`
  background: #fff;
  border-top: 1px solid ${BORDER};
  padding: 12px 24px;
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 12px;
  flex-wrap: wrap;
  .ant-btn { border-radius: 8px; height: 38px; }

  @media (max-width: 640px) {
    padding: 10px 14px;
    gap: 8px;
    > * { flex: 1 1 auto; }
    .ant-btn { height: 40px; flex: 1; }
    .footer-actions {
      display: flex; gap: 8px; flex: 1 1 100%;
    }
  }
`;

export const Card = styled.div`
  background: #fff;
  border: 1px solid ${BORDER};
  border-radius: 12px;
  padding: 20px 24px;
  margin-bottom: 16px;

  h3 { margin: 0 0 4px; color: ${PRIMARY}; font-size: 15px; font-weight: 600; }
  .desc { color: ${MUTED}; font-size: 13px; margin-bottom: 16px; }
  .row { display: grid; gap: 14px; grid-template-columns: 1fr 1fr; }
  .row > .full { grid-column: 1 / -1; }
  label { display: block; font-size: 12px; font-weight: 600; color: ${PRIMARY}; margin-bottom: 6px; text-transform: uppercase; letter-spacing: 0.5px; }
`;

export const TemplateGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(280px, 1fr));
  gap: 14px;
`;

export const TemplateCard = styled.div`
  position: relative;
  background: #fff;
  border: 1px solid ${BORDER};
  border-radius: 10px;
  padding: 16px;
  display: flex; flex-direction: column; gap: 8px;
  transition: all 0.15s ease;
  cursor: pointer;

  &:hover { border-color: ${ACCENT}; box-shadow: 0 4px 12px rgba(15,23,42,0.06); }
  .icon {
    width: 40px; height: 40px; border-radius: 10px;
    background: ${ACCENT_SOFT}; color: ${ACCENT};
    display: flex; align-items: center; justify-content: center;
    font-size: 18px;
  }
  .ttl { font-weight: 600; color: ${PRIMARY}; font-size: 14px; }
  .desc { color: ${MUTED}; font-size: 12px; flex: 1; }
  .meta { font-size: 11px; color: ${MUTED}; text-transform: uppercase; letter-spacing: 0.5px; }

  ${({ $selected }) =>
    $selected &&
    `
      border-color: ${ACCENT};
      box-shadow: 0 0 0 2px ${ACCENT_SOFT}, 0 4px 12px rgba(15,23,42,0.08);
      background: ${ACCENT_SOFT};
    `}

  .selected-badge {
    position: absolute;
    top: 10px;
    right: 10px;
    background: ${ACCENT};
    color: #fff;
    font-size: 10px;
    font-weight: 700;
    text-transform: uppercase;
    letter-spacing: 0.5px;
    padding: 3px 8px;
    border-radius: 999px;
  }
`;

export const SequenceLayout = styled.div`
  display: grid;
  grid-template-columns: 240px 1fr 320px;
  height: 100%;
  background: ${BG};

  &.no-config { grid-template-columns: 240px 1fr; }
`;

export const Palette = styled.div`
  background: #fff;
  border-right: 1px solid ${BORDER};
  padding: 16px;
  overflow: auto;

  h4 {
    font-size: 11px; text-transform: uppercase; letter-spacing: 0.6px;
    color: ${MUTED}; margin: 14px 0 8px; font-weight: 700;
  }
  h4:first-child { margin-top: 0; }
`;

export const PaletteItem = styled.div`
  border: 1px solid ${BORDER};
  border-radius: 8px;
  padding: 10px 12px;
  margin-bottom: 8px;
  cursor: grab;
  background: #fff;
  display: flex; align-items: center; gap: 10px;
  transition: all 0.15s ease;
  font-size: 13px;
  color: ${PRIMARY};

  &:hover { border-color: ${ACCENT}; background: ${ACCENT_SOFT}; }
  .icon {
    width: 28px; height: 28px; border-radius: 6px;
    display: inline-flex; align-items: center; justify-content: center;
    background: #f1f5f9; color: ${PRIMARY};
    font-size: 12px;
  }
  .badge {
    margin-left: auto;
    font-size: 9px; padding: 2px 6px; border-radius: 999px;
    background: #fef3c7; color: #92400e; font-weight: 600;
    text-transform: uppercase; letter-spacing: 0.4px;
  }
`;

export const Canvas = styled.div`
  position: relative;
  background:
    radial-gradient(circle, #d1d5db 1px, transparent 1px) 0 0/20px 20px,
    ${BG};

  .react-flow__node {
    background: #fff;
    border: 1px solid ${BORDER};
    border-radius: 10px;
    padding: 10px 14px;
    font-size: 13px;
    color: ${PRIMARY};
    box-shadow: 0 1px 3px rgba(15,23,42,0.06);
    min-width: 180px;
  }
  .react-flow__node.selected { border-color: ${ACCENT}; box-shadow: 0 0 0 3px ${ACCENT_SOFT}; }
  .react-flow__handle { background: ${ACCENT}; width: 8px; height: 8px; border: 2px solid #fff; }
  .node-title { font-weight: 600; display: flex; align-items: center; gap: 8px; }
  .node-sub { font-size: 11px; color: ${MUTED}; margin-top: 4px; }
`;

export const ConfigPanel = styled.div`
  background: #fff;
  border-left: 1px solid ${BORDER};
  padding: 18px;
  overflow: auto;

  h3 { margin: 0 0 4px; font-size: 15px; color: ${PRIMARY}; font-weight: 600; }
  .sub { color: ${MUTED}; font-size: 12px; margin-bottom: 16px; }
  label { display: block; font-size: 11px; font-weight: 700; color: ${PRIMARY}; margin: 14px 0 6px; text-transform: uppercase; letter-spacing: 0.5px; }
  label:first-of-type { margin-top: 0; }
  .empty { color: ${MUTED}; font-size: 13px; text-align: center; padding: 40px 12px; }
  .coming {
    background: #fef3c7; color: #92400e;
    padding: 10px 12px; border-radius: 8px; font-size: 12px;
    margin-top: 12px;
  }
`;

export const FilterRow = styled.div`
  display: grid;
  grid-template-columns: 1fr 140px 1fr auto;
  gap: 8px;
  margin-bottom: 8px;
  align-items: center;
`;

export const TokenChip = styled.span`
  display: inline-block;
  background: #f1f5f9;
  color: ${PRIMARY};
  border: 1px solid ${BORDER};
  border-radius: 999px;
  padding: 3px 10px;
  font-size: 11px;
  font-family: ui-monospace, SFMono-Regular, Menlo, monospace;
  margin: 2px 4px 2px 0;
`;
